import { Modal, Table, Card, Row, Col, Typography, Input, Select, message } from "antd";
import { toLocalDateTime, postData, showError } from "../../../utils";
import { useState, useEffect } from "react";
import { useBlockScreen, useRoot } from "../../../rootContext";

const { Text } = Typography;
const { Option } = Select;

const ReviewOrderModal = ({
    visible,
    onCancel,
    order,
    suppliers,
    blocker,
    root
}) => {
    const [editedItems, setEditedItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [shippingQuote, setShippingQuote] = useState(null);
    // initialize as an empty array (will hold non-PGD shipping options)
    const [shippingServiceOptions, setShippingServiceOptions] = useState([]);
    const [cachedShippingOptions, setCachedShippingOptions] = useState({});
    const [hasInitialized, setHasInitialized] = useState(false);
    // leave selectedShipServiceLevel empty until an option is loaded
    const [selectedShipServiceLevel, setSelectedShipServiceLevel] = useState("");
    // New insurance state variable
    const [insurance, setInsurance] = useState(0);
    const [endUserPO, setEndUserPO] = useState('');

    useEffect(() => {
        if (visible && !hasInitialized && order?.Items) {
            setEndUserPO('');
            // Set default supplier for each line item from the first supplier available
            setEditedItems(
                order.Items.map(item => ({
                    ...item,
                    selectedSupplier: suppliers[0]?.key,
                    QuantityOrdered: item.QuantityPurchased,
                    Suppliers: item.Suppliers || {}
                }))
            );
            setHasInitialized(true);
            // Call fetchShippingQuote with the default supplier from the first row.
            const defaultSupplier = suppliers[0]?.key;
            if (defaultSupplier) {
                // Check if the default supplier is 'scansource' and that every order item has zero available quantity.
                if (defaultSupplier === 'Ss' && order.Items.every(item => (item.Suppliers?.[defaultSupplier]?.Qty ?? 0) === 0)) {
                    // Skip calling shipping quote; you might want to clear any shipping options.
                    setShippingServiceOptions([]);
                    setSelectedShipServiceLevel("");
                } else if (cachedShippingOptions[defaultSupplier]) {
                    setShippingServiceOptions(cachedShippingOptions[defaultSupplier]);
                    setSelectedShipServiceLevel(cachedShippingOptions[defaultSupplier]?.[0]?.value || "");
                } else {
                    fetchShippingQuote(defaultSupplier);
                }
            }

        } else if (!visible) {
            setHasInitialized(false);
        }
    }, [visible, order, suppliers]); // eslint-disable-line react-hooks/exhaustive-deps

    // In the fetchShippingQuote function - Update cache to store full shipping quote
    const fetchShippingQuote = async (supplier) => {
        try {
            const response = await postData(
                `${window.SiteBasePath}/api/OrdersAutomation/GetShippingQuote`,
                order,
                {
                    blocker, root,
                    headers: {
                        'Selected-Supplier': supplier
                    }
                }
            );

            // Store service options, insurance, AND full shipping quote in cache
            const serviceOptions = response.Items.map(item => ({
                value: item.ServiceLevelCode,
                label: item.ServiceLevelDescription
            }));

            const filteredOptions = serviceOptions.filter(option => option.value !== 'PGD');
            const newCacheEntry = {
                options: filteredOptions,
                insurance: response?.Insurance || 0,
                shippingQuote: response // Store the full response
            };

            setCachedShippingOptions(prev => ({
                ...prev,
                [supplier]: newCacheEntry
            }));

            // Update all related states
            setShippingServiceOptions(filteredOptions);
            setInsurance(newCacheEntry.insurance);
            setSelectedShipServiceLevel(filteredOptions?.[0]?.value || "");
            setShippingQuote(response); // Update shipping quote state

        } catch (error) {
            showError("Failed to fetch shipping quote", error);
        }
    };

    const handleSupplierChange = (itemIndex, value) => {
        const newItems = editedItems.map(item => ({
            ...item,
            selectedSupplier: value
        }));
        setEditedItems(newItems);

        if (value === 'Ss' && newItems.every(item => (item.Suppliers?.[value]?.Qty ?? 0) === 0)) {
            setShippingServiceOptions([]);
            setSelectedShipServiceLevel("");
            setInsurance(0);
            setShippingQuote(null);
        } else if (cachedShippingOptions[value]) {
            setShippingServiceOptions(cachedShippingOptions[value].options);
            setInsurance(cachedShippingOptions[value].insurance);
            setShippingQuote(cachedShippingOptions[value].shippingQuote);
            setSelectedShipServiceLevel(cachedShippingOptions[value].options?.[0]?.value || "");
        } else if (value === "Bs") {
            const hardcodedOptions = [
                { value: 'FedEx-LTL-Ground', label: 'FedEx-LTL-Ground' },
            ];
            setShippingServiceOptions(hardcodedOptions);
            setCachedShippingOptions(prev => ({
                ...prev,
                [value]: {
                    options: hardcodedOptions,
                    insurance: 0,
                    shippingQuote: null
                }
            }));
            setSelectedShipServiceLevel(hardcodedOptions[0]?.value || "");
            setInsurance(0);
            setShippingQuote(null);
        } else {
            fetchShippingQuote(value);
        }
    };



    const handleQuantityChange = (itemIndex, value) => {
        const newItems = [...editedItems];
        const numericValue = Math.max(0, parseInt(value) || 0);
        newItems[itemIndex].QuantityOrdered = Math.min(
            numericValue,
            newItems[itemIndex].QuantityPurchased
        );
        setEditedItems(newItems);
    };

    // Columns for supplier-specific nested data
    const supplierColumns = suppliers.map((supplier) => ({
        title: supplier.name,
        children: [
            {
                title: "Qty",
                dataIndex: ["Suppliers", supplier.key, "Qty"],
                key: `${supplier.key}Qty`,
                align: "center",
                render: (qty) => qty ?? 0
            },
            {
                title: "Price",
                dataIndex: ["Suppliers", supplier.key, "Price"],
                key: `${supplier.key}Price`,
                align: "center",
                render: (price) => `$${(price ?? 0).toFixed(2)}`
            }
        ]
    }));

    const columns = [
        {
            title: "SKU",
            dataIndex: "SKU",
            key: "SKU",
            width: 150,
        },
        {
            title: "Product Name",
            dataIndex: "ProductName",
            key: "ProductName",
        },
        {
            title: "Earning",
            dataIndex: "Earning",
            key: "Earning",
            width: 150,
        },
        {
            title: "Select Supplier",
            key: "selectedSupplier",
            width: 200,
            render: (_, record, index) => (

                <Select
                    value={editedItems[0]?.selectedSupplier}
                    onChange={(value) => handleSupplierChange(0, value)} // just reuse index 0
                    style={{ width: '100%' }}
                >
                    {suppliers.map(supplier => (
                        <Option key={supplier.key} value={supplier.key}>
                            {supplier.name}
                        </Option>
                    ))}
                </Select>


            ),
        },
        {
            title: "Purchased Quantity",
            dataIndex: "QuantityPurchased",
            key: "QuantityPurchased",
            width: 150,
            align: "center",
        },
        {
            title: "Order Quantity",
            key: "QuantityOrdered",
            width: 200,
            render: (_, record, index) => (
                <Input
                    type="number"
                    min={0}
                    max={record.QuantityPurchased}
                    value={record.QuantityOrdered}
                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                    style={{ width: '100%' }}
                />
            ),
        },
        ...supplierColumns,
    ];

    const rowClassName = (record) => {
        const availableQty = record.Suppliers?.[record.selectedSupplier]?.Qty ?? 0;
        return record.QuantityOrdered > availableQty ? 'red-row' : '';
    };

    const handlePostOrder = async () => {
        setLoading(true);
        setError(null);

        // Validate shipping service option selection.
        if (!selectedShipServiceLevel) {
            setError("Please select a valid shipping service option before posting the order.");
            setLoading(false);
            return;
        }

        // Validate that all items have the same supplier.
        const uniqueSuppliers = new Set(editedItems.map(item => item.selectedSupplier));
        if (uniqueSuppliers.size > 1) {
            setError("All items must have the same supplier.");
            setLoading(false);
            return;
        }

        // Validate that ordered quantities do not exceed available quantities.
        const invalidItems = editedItems.filter(item => {
            const availableQty = item.Suppliers?.[item.selectedSupplier]?.Qty ?? 0;
            return item.QuantityOrdered > availableQty;
        });
        if (invalidItems.length > 0) {
            const invalidSkus = invalidItems.map(item => item.SKU).join(', ');
            setError(`Some items have ordered quantities greater than the available quantities. SKUs: ${invalidSkus}`);
            setLoading(false);
            return;
        }

        // Validate no item is ordered with zero quantity.
        const zeroQuantityItems = editedItems.filter(item => item.QuantityOrdered === 0);
        if (zeroQuantityItems.length > 0) {
            setError("Order cannot be posted with zero quantity items.");
            setLoading(false);
            return;
        }

        try {
            const orderData = {
                ...order,
                ShipServiceLevel: selectedShipServiceLevel,
                EndUserPO: endUserPO,
                Items: editedItems.map(item => ({
                    ...item,
                    Supplier: item.selectedSupplier,
                    Price: item.Suppliers?.[item.selectedSupplier]?.Price,
                    Quantity: item.QuantityOrdered,
                    SKU: item.SKU,
                    ProductName: item.ProductName,
                    QuantityPurchased: item.QuantityPurchased
                }))
            };

            console.log("Submitting order data:", orderData);
            await postData(
                `${window.SiteBasePath}/api/OrdersAutomation/PostOrder`,
                orderData,
                {
                    blocker,
                    root,
                    headers: {
                        'Selected-Supplier': editedItems[0]?.selectedSupplier
                    }
                }
            );
            message.success("Order posted successfully!");
            onCancel();
            setHasInitialized(false);
        } catch (error) {
            setError("Failed to post order. Please try again.");
            showError("Order posting failed", error);
        } finally {
            setLoading(false);
        }
    };

    if (!order || !visible) return null;

    const totalProductCost = editedItems.reduce((sum, item) => {
        const supplierPrice = item.Suppliers?.[item.selectedSupplier]?.Price ?? 0;
        return sum + (supplierPrice * item.QuantityOrdered);
    }, 0);
    const totalEarnings = editedItems.reduce((sum, item) => sum + parseFloat(item.Earning ?? 0), 0);
    // Add this line near the top of the component to get the current supplier
    const currentSupplier = editedItems[0]?.selectedSupplier;

    const freightAmount = currentSupplier === 'Bs'
        ? 0
        : (shippingQuote?.Items?.filter(item => item.ServiceLevelCode === selectedShipServiceLevel)
            .reduce((sum, item) => sum + item.FreightAmount, 0) || 0);

    const totalPrice = totalProductCost + freightAmount + insurance;

    return (
        <Modal
            title="Review Order Before Posting"
            visible={visible}
            width={1200}
            onCancel={onCancel}
            onOk={handlePostOrder}
            okText={loading ? "Posting..." : "Confirm Post"}
            cancelText="Cancel"
            confirmLoading={loading}
        >
            {error && (
                <div style={{ color: 'red', marginBottom: 16 }}>
                    {error}
                </div>
            )}

            <Card title="Order Summary" size="small">
                <Row gutter={16}>
                    <Col span={8}>
                        <Text strong>Order ID:</Text> {order?.OrderId}
                    </Col>
                    <Col span={8}>
                        <Text strong>Buyer:</Text> {order?.BuyerName} ({order?.BuyerEmail})
                    </Col>
                    <Col span={8}>
                        <Text strong>Order Date:</Text> {toLocalDateTime(order?.PurchaseDate)}
                    </Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 8 }}>
                    <Col span={8}>
                        <Text strong>Shipping Service:</Text>
                        <Select
                            value={selectedShipServiceLevel}
                            onChange={value => setSelectedShipServiceLevel(value)}
                            style={{ width: '100%' }}
                        >
                            {shippingServiceOptions.map(option => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={8}>
                        <Text strong>End User PO:</Text>
                        <Input
                            value={endUserPO}
                            onChange={(e) => setEndUserPO(e.target.value)}
                            placeholder="Enter PO number"
                            style={{ width: '100%' }}
                        />
                    </Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 8 }}>
                    <Col span={8}>
                        <Text strong>Total Items:</Text> {order?.Items?.length || 0}
                    </Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 8 }}>
                    <Col span={24}>
                        <Text strong>Shipping Address:</Text>
                        <div>
                            {order?.ShipAddress1 && <div>{order.ShipAddress1}</div>}
                            {order?.ShipAddress2 && <div>{order.ShipAddress2}</div>}
                            {order?.ShipAddress3 && <div>{order.ShipAddress3}</div>}
                            <div>{order?.ShipCity}, {order?.ShipState} {order?.ShipPostalCode}</div>
                            <div>{order?.ShipCountry}</div>
                            {order?.ShipPhoneNumber && <div>Phone: {order.ShipPhoneNumber}</div>}
                        </div>
                    </Col>
                </Row>
            </Card>

            <Card title="Items Allocation" size="small" style={{ marginTop: 16 }}>
                <Table
                    columns={columns}
                    dataSource={editedItems}
                    pagination={false}
                    scroll={{ x: "max-content" }}
                    bordered
                    rowKey="OrderItemId"
                    rowClassName={rowClassName}
                />
                <div style={{ marginTop: 16 }}>
                    <Row gutter={16}>
                        <Col span={22} align="right">
                            <Text strong>Total Ordered Quantity:</Text>
                        </Col>
                        <Col span={2} align="right">
                            <Text strong>
                                {editedItems.reduce((sum, item) => sum + item.QuantityOrdered, 0)}
                            </Text>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={22} align="right">
                            <Text strong>Products Cost:</Text>
                        </Col>
                        <Col span={2} align="right">
                            <Text strong>${totalProductCost.toFixed(2)}</Text>
                        </Col>
                    </Row>

                    {currentSupplier !== 'Bs' && (
                        <Row gutter={16}>
                            <Col span={22} align="right">
                                <Text strong>Freight Amount:</Text>
                            </Col>
                            <Col span={2} align="right">
                                <Text strong>${freightAmount.toFixed(2)}</Text>
                            </Col>
                        </Row>
                    )}

                    {currentSupplier !== 'Bs' && (
                        <Row gutter={16}>
                            <Col span={22} align="right">
                                <Text strong>Insurance:</Text>
                            </Col>
                            <Col span={2} align="right">
                                <Text strong>${insurance.toFixed(2)}</Text>
                            </Col>
                        </Row>
                    )}
                    <Row gutter={16}>
                        <Col span={22} align="right">
                            <Text strong>Total Earnings:</Text>
                        </Col>
                        <Col span={2} align="right">
                            <Text strong>${totalEarnings.toFixed(2)}</Text>
                        </Col>
                    </Row>
                    <Row gutter={16} style={{ backgroundColor: '#fafafa' }}>
                        <Col span={22} align="right">
                            <Text strong>Total Price:</Text>
                        </Col>
                        <Col span={2} align="right">
                            <Text strong type="danger">${totalPrice.toFixed(2)}</Text>
                        </Col>
                    </Row>
                </div>
            </Card>
        </Modal>
    );
};

export default ReviewOrderModal;
